import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { erp } from "@/lib/erp";
import type { Item } from "@/lib/erp";
import { computeDirtyState } from "../item-detail/utils/computeDirtyState";
import { itemQueryKeys } from "./itemQueryKeys";

/**
 * Mutation input type for save operations
 * Can be either:
 * - currentData: full document (for regular saves)
 * - { patch, currentData }: patch-based save (for modal saves)
 */
type SaveItemInput = 
  | Record<string, any> // currentData only
  | { patch: Record<string, any>; currentData: Record<string, any> }; // patch-based

/**
 * Hook for updating an existing item
 * 
 * Supports two modes:
 * 1. Regular save: computes diff from originalData vs currentData
 * 2. Patch-based save: uses provided patch directly (avoids recomputing diff)
 * 
 * @param name - Item name/code to update
 * @param originalData - Original data from API (used to compute diff for regular saves)
 * @returns React Query mutation for saving item
 */
export function useSaveItem(name: string, originalData: Record<string, any>) {
  const queryClient = useQueryClient();

  // Keep a ref to latest originalData to avoid stale closure issues
  const originalDataRef = useRef(originalData);
  useEffect(() => {
    originalDataRef.current = originalData;
  }, [originalData]);

  return useMutation<Item, Error, SaveItemInput>({
    mutationFn: async (input: SaveItemInput) => {
      // Check if this is a patch-based save
      const isPatchSave = input && typeof input === 'object' && 'patch' in input;
      
      if (isPatchSave) {
        const { patch, currentData } = input as { patch: Record<string, any>; currentData: Record<string, any> };
        
        console.log('[useSaveItem] Patch-based save:', {
          patchKeys: Object.keys(patch),
          patch,
        });
        
        // CRITICAL INVARIANT: patch non-empty cannot become "no changes"
        // If patch is provided, we MUST send it to the server
        if (Object.keys(patch).length === 0) {
          console.log('[useSaveItem] Patch is empty, returning currentData');
          return Promise.resolve(currentData as Item);
        }
        
        // For patch saves, send patch directly (don't recompute diff)
        // This avoids issues where originalDataRef might have been mutated optimistically
        console.log('[useSaveItem] Sending patch to server:', patch);
        const updated = await erp.items.update(name, patch);
        
        // CRITICAL: Server echo validation - verify server actually persisted the patch
        // If server didn't apply patch, treat as failure (prevents silent snap-back)
        const validationErrors: string[] = [];
        for (const [key, expectedValue] of Object.entries(patch)) {
          const actualValue = (updated as any)?.[key];
          
          // Deep comparison for objects/arrays
          const valuesMatch = JSON.stringify(actualValue) === JSON.stringify(expectedValue);
          
          if (!valuesMatch) {
            validationErrors.push(
              `Field "${key}": expected ${JSON.stringify(expectedValue)} but got ${JSON.stringify(actualValue)}`
            );
          }
        }
        
        if (validationErrors.length > 0) {
          const errorMessage = `Server did not persist changes:\n${validationErrors.join('\n')}`;
          console.error('[useSaveItem] Server echo validation failed:', errorMessage);
          throw new Error(errorMessage);
        }
        
        console.log('[useSaveItem] Server echo validation passed - all patch fields confirmed');
        return updated;
      }
      
      // Regular save: compute diff from originalData
      const currentData = input as Record<string, any>;
      const latestOriginal = originalDataRef.current;
      
      // Compute diff - only send changed fields
      const changes = computeDirtyState(latestOriginal, currentData);
      
      // Debug logging
      console.log('[useSaveItem] Computing diff:', {
        originalKeys: Object.keys(latestOriginal).length,
        currentKeys: Object.keys(currentData).length,
        changesKeys: Object.keys(changes),
        changes,
      });
      
      // If no changes, return current data (no API call needed)
      if (Object.keys(changes).length === 0) {
        console.log('[useSaveItem] No changes detected, skipping API call');
        return Promise.resolve(currentData as Item);
      }
      
      console.log('[useSaveItem] Sending update to server:', changes);
      
      // Send only changed fields to server
      return erp.items.update(name, changes);
    },
    onMutate: async (input) => {
      // Optimistic cache update for patch-based saves
      // This prevents refetch from overwriting optimistic UI updates
      const isPatchSave = input && typeof input === 'object' && 'patch' in input;
      
      if (isPatchSave) {
        const { patch, currentData } = input as { patch: Record<string, any>; currentData: Record<string, any> };
        
        // Cancel any outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries({ queryKey: itemQueryKeys.detail(name) });
        
        // Snapshot the previous value
        const previous = queryClient.getQueryData<Item>(itemQueryKeys.detail(name));
        
        // Optimistically update cache with merged data
        const optimisticData = { ...(previous || currentData || {}), ...patch };
        queryClient.setQueryData(itemQueryKeys.detail(name), optimisticData);
        
        console.log('[useSaveItem] Optimistic cache update applied:', {
          patchKeys: Object.keys(patch),
          previousKeys: previous ? Object.keys(previous).length : 0,
          optimisticKeys: Object.keys(optimisticData).length,
        });
        
        // Return context for rollback
        return { previous };
      }
      
      return undefined;
    },
    onError: (_error, _input, context) => {
      // Rollback optimistic update on error
      if (context && typeof context === 'object' && 'previous' in context && context.previous) {
        console.log('[useSaveItem] Rolling back optimistic cache update due to error');
        queryClient.setQueryData(itemQueryKeys.detail(name), context.previous as Item);
      }
    },
    onSuccess: (updatedItem) => {
      // CRITICAL: Update cache with server response AFTER echo-validation passes
      // This ensures cache reflects server truth
      queryClient.setQueryData(itemQueryKeys.detail(name), updatedItem);
      
      console.log('[useSaveItem] Cache updated with server response:', {
        updatedItemKeys: Object.keys(updatedItem).length,
      });
      
      // Only invalidate AFTER cache is updated (prevents snap-back)
      queryClient.invalidateQueries({ queryKey: itemQueryKeys.detail(name) });
    },
  });
}

